import { ArticleJsonLd } from 'next-seo'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ISbStoryData } from 'storyblok-js-client'

import { Seo } from 'components/Seo'
import { Editable } from 'components/blocks/deprecated/Editable'
import { Layout } from 'components/blocks/layout/Layout'
import { Empty } from 'components/blocks/sections/directories/AllEntries/Empty'
import { Loader } from 'components/blocks/sections/directories/AllEntries/Loader'
import { ContentThumb } from 'components/blocks/sections/directories/thumbs/ContentThumb'
import { Icon, Tag } from 'components/ui'
import { TagList } from 'components/ui/TagList'
import VideoOverlay from 'components/ui/VideoOverlay'
import { ButtonLink } from 'components/ui/deprecated/ButtonLink'
import { Container } from 'components/ui/deprecated/Layout'
import { SocialShare } from 'components/ui/deprecated/SocialShare'
import { SITE_ORIGIN } from 'lib/constants'
import { useDataContext } from 'lib/contexts/dataContext'
import { useTranslation } from 'lib/i18n'
import { Storyblok } from 'lib/storyblok'
import { BlogPostStoryblok, DoctorStoryblok } from 'lib/storyblok/types'
import {
  getStoryblokCacheValue,
  setStoryblokCacheValue,
} from 'lib/utils/content'
import { formatDateLocale } from 'lib/utils/datetime'
import { isSourceType } from 'lib/utils/isSourceType'
import { removeTrailingSlash } from 'lib/utils/string'

import { StoryblokImage } from '../StoryblokImage'
import { StoryblokLink } from '../StoryblokLink'
import { StoryblokRichText } from '../rich-text/StoryblokRichText'

type Props = {
  story: ISbStoryData<BlogPostStoryblok>
}

export const getTags = (content: unknown): string[] => {
  if (Array.isArray(content)) {
    return content.map(
      (cat: { content: { name: string } }) => cat.content?.name
    )
  }
  if (typeof content === 'string' && content.length > 0) {
    return [content]
  }

  if (typeof content === 'object' && content && 'content' in content) {
    return [(content as { content: { name: string } }).content.name]
  }

  return []
}

export const BlogPostPage = ({ story }: Props): JSX.Element => {
  const { i18n, locale } = useTranslation()
  const sideBarRef = useRef<HTMLDivElement>(null)
  const [sideBarHeight, setSidebarHeight] = useState<number>(0)
  const { config } = useDataContext()
  const {
    query: { lang },
    isPreview,
  } = useRouter()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<ISbStoryData<BlogPostStoryblok>[] | null>(
    null
  )
  const [error, setError] = useState(false)
  const [videoOverlay, setVideoOverlay] = useState<string | null>(null)

  const fetchRelatedPosts = useCallback(async () => {
    setLoading(true)
    setError(false)

    try {
      const {
        data: { stories, cv },
      } = await Storyblok.get('cdn/stories', {
        page: 1,
        per_page: 3,
        version: isPreview ? 'draft' : 'published',
        starts_with: `${lang}/`,
        sort_by: 'first_published_at:desc',
        content_type: 'blog-post',
        resolve_relations: 'blog-post.services,',
        cv: getStoryblokCacheValue(),
        excluding_ids: story.id.toString(),
      })

      setData(stories)
      setStoryblokCacheValue(cv)
    } catch {
      setError(true)
    }

    setLoading(false)
  }, [isPreview, story])

  useEffect(() => {
    fetchRelatedPosts()
  }, [fetchRelatedPosts])

  useEffect(() => {
    const updateSidebarHeight = () => {
      if (sideBarRef?.current?.clientHeight) {
        setSidebarHeight(sideBarRef.current.clientHeight)
      }
    }

    updateSidebarHeight()
    window.addEventListener('resize', updateSidebarHeight)

    return () => window.removeEventListener('resize', updateSidebarHeight)
  }, [])

  const categories = story.content.services && getTags(story.content.services)

  return (
    <Layout>
      {story.content.schema && story.content.schema.code && (
        <Script type="application/ld+json">{story.content.schema.code}</Script>
      )}
      <Seo story={story} />
      <ArticleJsonLd
        title={
          story.content?.seo?.title
            ? JSON.stringify(story.content.seo.title).slice(1, -1)
            : ''
        }
        authorName="TFP"
        publisherName="TFP"
        publisherLogo={config?.content.menu_logo?.filename || ''}
        description={
          story.content?.seo?.description
            ? JSON.stringify(story.content.seo.description).slice(1, -1)
            : ''
        }
        datePublished={story.created_at}
        url={
          story.content?.canonical_url ||
          removeTrailingSlash(`${SITE_ORIGIN}${story.full_slug}`)
        }
        images={
          story.content?.seo?.og_image || story.content?.seo?.twitter_image
            ? [
                (story.content?.seo?.og_image ||
                  story.content?.seo?.twitter_image) as string,
              ]
            : []
        }
      />

      <div className="flex flex-col items-center pb-20">
        {videoOverlay && (
          <VideoOverlay
            videoUrl={videoOverlay}
            setVideoOverlay={setVideoOverlay}
          />
        )}
        <div className="w-full h-[min(480px,50vw)]">
          {story.content.cover_image &&
            (!story.content.cover_media ||
              story.content.cover_media?.length === 0) && (
              <StoryblokImage
                asset={story.content.cover_image}
                className="w-full h-full object-cover"
              />
            )}
          {story.content.cover_media &&
            story.content.cover_media.length > 0 &&
            story.content.cover_media.map((asset, i) =>
              isSourceType(asset.media.filename, 'image') ? (
                <StoryblokImage
                  asset={asset.media}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div key={i} className="group relative w-full h-full">
                  <video
                    muted
                    playsInline
                    loop
                    autoPlay
                    disablePictureInPicture
                    className="h-full w-full object-cover"
                  >
                    <source src={asset.media.filename} />
                  </video>
                  <button
                    onClick={() =>
                      asset.video_youtube_link
                        ? setVideoOverlay(asset.video_youtube_link)
                        : setVideoOverlay(asset.media.filename)
                    }
                    className="absolute right-2 bottom-2 rounded-full bg-white/80 opacity-100 md:opacity-0 md:transition-opacity md:hover:bg-white md:group-hover:opacity-100 flex items-center justify-center p-2 border-none"
                    aria-label="Open video"
                  >
                    <Icon icon="expand" size={16} />
                  </button>
                </div>
              )
            )}
        </div>

        <div className="w-full">
          <div
            className="hidden mt-4 sticky top-20 w-fit z-[500] xl:block xl:mt-0"
            ref={sideBarRef}
          >
            <Container>
              <div className="max-w-fit bg-white px-4 py-3 rounded-3 shadow-sm">
                <span className="text-sixteen">{i18n('share')}:</span>
                <SocialShare className="mt-3" />
              </div>
            </Container>
          </div>

          <div
            className="flex flex-col items-center max-w-[49rem] mx-auto px-5 py-10 md:py-15 text-center -mt-[var(--sideBarHeight)]"
            style={
              { '--sideBarHeight': `${sideBarHeight}px` } as React.CSSProperties
            }
          >
            <div className="flex items-center gap-3 pb-5">
              {categories && (
                <TagList hasHiddenTags maxVisibleTags={2} tags={categories} />
              )}

              <div className="text-fourteen flex items-center gap-1">
                {story.content.author && <span>{story.content.author}</span>}
                {story.content.author && story.first_published_at && (
                  <span>⋅</span>
                )}
                {story.first_published_at && (
                  <span>
                    {formatDateLocale(story.first_published_at, locale)}
                  </span>
                )}
              </div>
            </div>

            <h1 className="mb-8 text-title-large md:text-title font-regular">
              {story.content.title}
            </h1>
            <SocialShare />
          </div>

          <div className="relative z-[100]">
            {story.content.content && (
              <Editable block={story.content}>
                <StoryblokRichText document={story.content.content} />
              </Editable>
            )}

            <Container variant="typography" className="mt-5">
              {story.content.reviewer_doctor && (
                <ReviewedByCard
                  cardLabel={
                    story.content.doctor_card_label ||
                    i18n('reviewedByCardLabel')
                  }
                  reviewerDoctor={
                    story.content.reviewer_doctor as unknown as DoctorStoryblok
                  }
                />
              )}
              <span className="text-sixteen font-regular">
                {i18n('likeTheArticle')}
              </span>
              <SocialShare className="mt-3" />
            </Container>
          </div>
        </div>
      </div>

      <Container>
        {loading && !data?.length && <Loader />}
        {data?.length ? (
          <div className="flex flex-col gap-12 z-[2] w-full pt-10 pb-10 md:pb-15">
            <div className="flex flex-col">
              <Tag>{i18n('blogPostsSectionTag')}</Tag>

              <div className="flex flex-col gap-4 justify-between mt-4 lg:flex-row">
                <h3 className="text-title-large font-regular">
                  {i18n('blogPostsSectionTitle')}
                </h3>
                <ButtonLink
                  variant="ghost"
                  href={`/${lang}/blog`}
                  className="w-fit"
                >
                  {i18n('blogPostsSectionSeeAll')}
                </ButtonLink>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-y-5 gap-x-12 z-[2] md:grid-cols-2 lg:grid-cols-3">
              {data?.map((item) => (
                <ContentThumb item={item} key={item.id} />
              ))}
            </div>
          </div>
        ) : (
          <Empty className="w-full" isError={error} />
        )}
      </Container>
    </Layout>
  )
}

type ReviewedByCardProps = {
  cardLabel: string
  reviewerDoctor: DoctorStoryblok
}

const ReviewedByCard = ({ cardLabel, reviewerDoctor }: ReviewedByCardProps) => {
  const { name, photo, job_title, clinic, short_bio, page_link } =
    reviewerDoctor.content

  return (
    <div className="flex flex-col gap-3 my-10">
      <p className="text-sixteen font-regular">{cardLabel}</p>
      <StoryblokLink
        link={page_link}
        className="p-4 lg:p-5 bg-foreground/5 flex flex-col gap-3 rounded-4 hover:bg-foreground/10"
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3">
          <div className="relative overflow-hidden w-10 h-10 rounded-full flex-shrink-0">
            {photo && (
              <StoryblokImage
                fill
                asset={photo}
                width={undefined}
                height={undefined}
                className="object-cover"
              />
            )}
          </div>
          <div className="flex flex-col gap-0.5 font-regular">
            <p className="text-eighteen text-foreground">{name}</p>
            <p className="text-sixteen text-foreground/60">
              {`${job_title}, ${clinic.name}`}
            </p>
          </div>
        </div>
        {short_bio && (
          <p className="font-regular text-sixteen lg:text-eighteen text-foreground/80">
            {short_bio}
          </p>
        )}
      </StoryblokLink>
    </div>
  )
}
